import React, { useState, useEffect } from 'react';
import {
  Drawer, Box, Typography, IconButton, Divider, Button, styled, useTheme, alpha,
} from '@mui/material';
import { DarkMode, LightMode, Palette, Close as CloseIcon, Save, Check } from '@mui/icons-material';
import { SketchPicker } from 'react-color';
import { motion } from 'framer-motion';
import { useThemeSettings } from '../context/ThemeSettingsContext';

const StyledSection = styled(Box)(({ theme }) => ({
  marginBottom: 24,
  padding: '16px',
  borderRadius: '14px',
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
}));

const OptionButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'selected' && prop !== 'selectedColor',
})(({ theme, selected, selectedColor }) => ({
  flex: 1,
  padding: '12px 16px',
  borderRadius: '10px',
  fontWeight: selected ? 700 : 500,
  border: `2px solid ${
    selected ? selectedColor : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
  }`,
  backgroundColor: selected
    ? theme.palette.mode === 'dark'
      ? 'rgba(255,255,255,0.05)'
      : 'rgba(0,0,0,0.02)'
    : 'transparent',
  color: selected ? selectedColor : theme.palette.text.primary,
  transition: 'transform 0.15s ease',
  '&:active': { transform: 'scale(0.97)' },
}));

const SectionTitle = styled(Typography)(() => ({
  fontFamily: 'var(--font-display)',
  fontWeight: 700,
  fontSize: '0.92rem',
  marginBottom: 12,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
}));

const PRESET_COLORS = ['#5B5FEF', '#1FBE93', '#FF6B6B', '#FFB020', '#EC4899', '#0EA5E9'];

// Simplified sibling of the admin SettingsSidebar: dark mode + primary color only.
const TeacherSettingsSidebar = ({ open, onClose }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { darkMode, primaryColor, updateSettings } = useThemeSettings();

  const [tempDarkMode, setTempDarkMode] = useState(darkMode);
  const [tempPrimaryColor, setTempPrimaryColor] = useState(primaryColor);
  const [showColorPicker, setShowColorPicker] = useState(false);

  useEffect(() => {
    if (open) {
      setTempDarkMode(darkMode);
      setTempPrimaryColor(primaryColor);
      setShowColorPicker(false);
    }
  }, [open, darkMode, primaryColor]);

  const hasChanges = tempDarkMode !== darkMode || tempPrimaryColor !== primaryColor;

  const handleSave = () => {
    updateSettings({ darkMode: tempDarkMode, primaryColor: tempPrimaryColor });
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '90vw', sm: 340 }, zIndex: 12000 } }}
      sx={{ zIndex: 12000 }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
        <Typography variant="h6" sx={{ fontFamily: 'var(--font-display)', fontWeight: 700 }}>
          Appearance
        </Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </Box>
      <Divider sx={{ opacity: 0.5, mb: 1 }} />

      <Box sx={{ px: 2, py: 1, flexGrow: 1 }}>
        <StyledSection>
          <SectionTitle><DarkMode fontSize="small" /> Display mode</SectionTitle>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <OptionButton
              selected={!tempDarkMode}
              selectedColor={primaryColor}
              onClick={() => setTempDarkMode(false)}
              startIcon={<LightMode />}
            >
              Light
            </OptionButton>
            <OptionButton
              selected={tempDarkMode}
              selectedColor={primaryColor}
              onClick={() => setTempDarkMode(true)}
              startIcon={<DarkMode />}
            >
              Dark
            </OptionButton>
          </Box>
        </StyledSection>

        <StyledSection>
          <SectionTitle><Palette fontSize="small" /> Theme color</SectionTitle>

          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            {PRESET_COLORS.map((c) => (
              <Box
                key={c}
                component={motion.button}
                whileTap={{ scale: 0.9 }}
                onClick={() => setTempPrimaryColor(c)}
                sx={{
                  width: 32, height: 32, borderRadius: '9px', backgroundColor: c, cursor: 'pointer',
                  border: `2px solid ${tempPrimaryColor.toLowerCase() === c.toLowerCase() ? theme.palette.text.primary : 'transparent'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', p: 0,
                }}
              >
                {tempPrimaryColor.toLowerCase() === c.toLowerCase() && (
                  <Check sx={{ fontSize: 16, color: '#fff' }} />
                )}
              </Box>
            ))}
          </Box>

          <Box
            onClick={() => setShowColorPicker(!showColorPicker)}
            sx={{
              p: 1,
              height: 40,
              borderRadius: '10px',
              backgroundColor: tempPrimaryColor,
              cursor: 'pointer',
              border: `2px solid ${alpha(primaryColor, 0.5)}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body2" sx={{ color: theme.palette.getContrastText(tempPrimaryColor), fontWeight: 600 }}>
              {tempPrimaryColor}
            </Typography>
          </Box>
          {showColorPicker && (
            <Box sx={{ mt: 2 }}>
              <SketchPicker
                color={tempPrimaryColor}
                onChangeComplete={(c) => setTempPrimaryColor(c.hex)}
                disableAlpha
              />
            </Box>
          )}
        </StyledSection>
      </Box>

      <Box sx={{ p: 2, borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}` }}>
        <Button
          variant="contained"
          fullWidth
          onClick={handleSave}
          disabled={!hasChanges}
          startIcon={<Save />}
          sx={{ py: 1.2, borderRadius: '10px', fontWeight: 700 }}
        >
          Save settings
        </Button>
      </Box>
    </Drawer>
  );
};

export default TeacherSettingsSidebar;
